import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
} from '@makeably/creativex-design-system';
import Asset from 'components/admin/review_queue/post_info/Asset';
import {
  getBoolDimension,
  getPathsDimension,
  getStatusDimension,
  NULL_LABEL,
} from 'components/internal/shared';
import { getItemContent } from 'utilities/item';
import {
  internalExplorerAdAccountPath,
  internalExplorerAdPath,
  internalExplorerAdCampaignPath,
  internalExplorerAuditAssetPath,
  internalExplorerFacebookAdAccountPath,
  internalExplorerFacebookAdCampaignPath,
  internalExplorerFacebookAdCreativePath,
  internalExplorerFacebookAdGroupPath,
} from 'utilities/routes';
import {
  decamelize,
  timestampToDate,
  toSpend,
} from 'utilities/string';
import styles from './Auditable.module.css';

const propTypes = {
  auditable: PropTypes.shape({
    adAccountId: PropTypes.number.isRequired,
    adCampaignId: PropTypes.number,
    adFormat: PropTypes.string,
    apiMediaType: PropTypes.string,
    auditAssetIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    auditPostStates: PropTypes.arrayOf(PropTypes.string).isRequired,
    brandName: PropTypes.string,
    channelName: PropTypes.string.isRequired,
    companyName: PropTypes.string.isRequired,
    facebookPageState: PropTypes.string,
    id: PropTypes.number.isRequired,
    importStatus: PropTypes.string,
    marketName: PropTypes.string,
    platformId: PropTypes.string.isRequired,
    publisherName: PropTypes.string,
    spend: PropTypes.number.isRequired,
    spendDateMax: PropTypes.string,
    spendDateMin: PropTypes.string,
  }).isRequired,
  bucket: PropTypes.string.isRequired,
  creativeUrl: PropTypes.string,
  isVideo: PropTypes.bool,
  type: PropTypes.string,
};

const defaultProps = {
  creativeUrl: undefined,
  isVideo: undefined,
  type: 'Ad',
};

const bmeValues = [
  {
    key: 'companyName',
    label: 'Company',
  },
  {
    key: 'brandName',
    label: 'Brand',
  },
  {
    key: 'marketName',
    label: 'Market',
  },
  {
    key: 'channelName',
    label: 'Channel',
  },
  {
    key: 'publisherName',
    label: 'Publisher',
  },
];

const metadata = [
  {
    key: 'apiMediaType',
    label: 'API Media Type',
  },
  {
    key: 'adFormat',
    label: 'Ad Format',
  },
];

const metrics = [
  {
    key: 'spend',
    label: 'Spend',
  },
  {
    key: 'spendDateMin',
    label: 'First Spend Date',
  },
  {
    key: 'spendDateMax',
    label: 'Last Spend Date',
  },
];

const statuses = [
  {
    key: 'bucket',
    label: 'Bucket',
  },
  {
    key: 'importStatus',
    label: 'Import Status',
  },
  {
    key: 'adCampaignParsingStatus',
    label: 'Ad Campaign Parsing Status',
  },
  {
    key: 'auditPostStates',
    label: 'Audit Post State',
  },
  {
    key: 'inflightChannelEnabled',
    label: 'Inflight Channel Enabled',
  },
  {
    key: 'facebookPageState',
    label: 'Facebook Page State',
  },
];

function getIds(type, typeLabel) {
  const adAccountLabel = type === 'Ad' ? 'Ad Account' : 'Facebook Ad Account';
  const adCampaignLabel = type === 'Ad' ? 'Ad Campaign' : 'Facebook Ad Campaign';

  return [
    {
      key: 'platformId',
      label: 'Platform ID',
    },
    {
      key: 'id',
      label: typeLabel,
    },
    {
      key: 'auditAssetIds',
      label: 'Audit Asset (CreativeX ID)',
    },
    {
      key: 'adCampaignId',
      label: adCampaignLabel,
    },
    {
      key: 'adAccountId',
      label: adAccountLabel,
    },
  ];
}

function getAdAccountPath(type) {
  switch (type) {
    case 'Ad':
      return internalExplorerAdAccountPath;
    default:
      return internalExplorerFacebookAdAccountPath;
  }
}

function getAdCampaignPath(type) {
  switch (type) {
    case 'Ad':
      return internalExplorerAdCampaignPath;
    default:
      return internalExplorerFacebookAdCampaignPath;
  }
}

function getAuditablePath(type) {
  switch (type) {
    case 'FacebookAdCreative':
      return internalExplorerFacebookAdCreativePath;
    case 'FacebookAdGroup':
      return internalExplorerFacebookAdGroupPath;
    default:
      return internalExplorerAdPath;
  }
}

function getItem({
  adAccountId,
  adCampaignId,
  adCampaignParsingStatus,
  adFormat,
  apiMediaType,
  auditAssetIds,
  auditPostStates,
  brandName,
  channelName,
  companyName,
  facebookPageState,
  id,
  importStatus,
  inflightChannelEnabled,
  marketName,
  platformId,
  publisherName,
  spend,
  spendDateMax,
  spendDateMin,
}, bucket, type) {
  return {
    adAccountId: getPathsDimension([adAccountId], getAdAccountPath(type)),
    adCampaignId: getPathsDimension([adCampaignId], getAdCampaignPath(type)),
    adCampaignParsingStatus: getStatusDimension(adCampaignParsingStatus),
    adFormat: { value: adFormat ?? NULL_LABEL },
    apiMediaType: { value: apiMediaType ?? NULL_LABEL },
    auditAssetIds: getPathsDimension(auditAssetIds, internalExplorerAuditAssetPath),
    auditPostStates: getStatusDimension(auditPostStates),
    brandName: { value: brandName },
    bucket: getStatusDimension(bucket),
    channelName: { value: channelName },
    companyName: { value: companyName },
    facebookPageState: getStatusDimension(facebookPageState),
    id: getPathsDimension([id], getAuditablePath(type)),
    importStatus: getStatusDimension(importStatus),
    inflightChannelEnabled: getBoolDimension(inflightChannelEnabled, true),
    marketName: { value: marketName },
    platformId: { value: platformId },
    publisherName: { value: publisherName ?? NULL_LABEL },
    spend: {
      label: toSpend(spend) ?? NULL_LABEL,
      value: spend,
    },
    spendDateMax: {
      label: timestampToDate(spendDateMax) ?? NULL_LABEL,
      value: spendDateMax,
    },
    spendDateMin: {
      label: timestampToDate(spendDateMin) ?? NULL_LABEL,
      value: spendDateMin,
    },
  };
}

function renderSection(name, item, selections) {
  return (
    <div>
      <h5 className="u-marginBottom-8">{ name }</h5>
      <div className="u-flexColumn u-gap-8 u-marginLeft-8">
        { selections.map(({ key, label }) => (
          <div key={key} className="u-flexRow u-gap-8">
            <span>
              { label }
              :
            </span>
            <h6>{ getItemContent(item, key) }</h6>
          </div>
        )) }
      </div>
    </div>
  );
}

function Auditable({
  auditable,
  bucket,
  creativeUrl,
  isVideo,
  type,
}) {
  const typeLabel = decamelize(type);
  const ids = getIds(type, typeLabel);
  const item = useMemo(() => getItem(auditable, bucket, type), [auditable, bucket, type]);

  return (
    <Card className="u-flexColumn u-gap-24">
      <h4>{ `${typeLabel} (${item.id.value}) - ${item.bucket.label}` }</h4>
      { renderSection('IDs', item, ids) }
      { renderSection('Brand Market Entity (BME)', item, bmeValues) }
      { renderSection('Ad Metadata', item, metadata) }
      { renderSection('Metrics', item, metrics) }
      { renderSection('Statuses', item, statuses) }
      <div className={styles.asset}>
        <h5 className="u-marginBottom-8">Asset Preview</h5>
        <Asset
          gridOptions={{}}
          isVideo={isVideo ?? false}
          sourceUrl={creativeUrl ?? ''}
        />
      </div>
    </Card>
  );
}

Auditable.propTypes = propTypes;
Auditable.defaultProps = defaultProps;

export default Auditable;
